import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Mail, Search, ChevronLeft, ChevronRight, Eye, CheckCircle, XCircle, Clock, AlertTriangle, Loader2, Trash2, AlertOctagon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_CONFIG = {
  sent: { label: "Sent", icon: CheckCircle, color: "bg-green-100 text-green-800" },
  failed: { label: "Failed", icon: XCircle, color: "bg-red-100 text-red-800" },
  pending: { label: "Pending", icon: Clock, color: "bg-yellow-100 text-yellow-800" },
  skipped: { label: "Skipped", icon: AlertTriangle, color: "bg-gray-100 text-gray-600" }
};

const CATEGORY_CONFIG = {
  logs: { label: "Logs", color: "bg-blue-100 text-blue-800" },
  reminder: { label: "Reminder", color: "bg-purple-100 text-purple-800" },
  CRM: { label: "CRM", color: "bg-indigo-100 text-indigo-800" },
  others: { label: "Others", color: "bg-slate-100 text-slate-700" }
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function CategoryBadge({ category }) {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.others;
  return (
    <Badge className={config.color}>
      {config.label}
    </Badge>
  );
}

function EmailDetailDialog({ log, open, onClose }) {
  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-500">To</label>
              <p className="text-sm font-mono">{log.to_email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Status</label>
              <div className="mt-1"><StatusBadge status={log.status} /></div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Category</label>
              <div className="mt-1"><CategoryBadge category={log.category} /></div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Date/Time</label>
              <p className="text-sm">{format(parseISO(log.created_at), "MMM d, yyyy 'at' h:mm a")}</p>
            </div>
            {log.cc_emails?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-500">CC</label>
                <p className="text-sm font-mono">{log.cc_emails.join(", ")}</p>
              </div>
            )}
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-500">Subject</label>
            <p className="text-sm font-semibold mt-1">{log.subject}</p>
          </div>

          {log.has_attachments && (
            <div>
              <label className="text-sm font-medium text-slate-500">Attachments</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {log.attachment_names?.map((name, i) => (
                  <Badge key={i} variant="outline">{name}</Badge>
                ))}
              </div>
            </div>
          )}

          {log.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <label className="text-sm font-medium text-red-700">Error</label>
              <p className="text-sm text-red-600 mt-1">{log.error_message}</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-500">Email Body</label>
            <div 
              className="mt-2 border rounded-lg p-4 bg-white max-h-96 overflow-y-auto prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: log.body_html }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirmDialog({ open, onClose, onConfirm, count, isAll }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertOctagon className="h-5 w-5" />
            {isAll ? "Delete All Email Logs" : "Delete Selected Logs"}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {isAll ? (
              <span className="text-red-600 font-medium">
                Warning: This will permanently delete ALL {count} email logs from the system. This action cannot be undone!
              </span>
            ) : (
              <span>
                Are you sure you want to delete {count} selected email log(s)? This action cannot be undone.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm}>
            <Trash2 className="mr-2 h-4 w-4" />
            {isAll ? "Delete All Logs" : "Delete Selected"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EmailLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [toEmailFilter, setToEmailFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [uniqueEmails, setUniqueEmails] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (toEmailFilter !== "all") params.append("to_email", toEmailFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (search) params.append("search", search);
      
      const res = await axios.get(`${API}/admin/email-logs?${params}`);
      setLogs(res.data.logs);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      
      // Update unique emails list
      if (res.data.unique_emails) {
        setUniqueEmails(res.data.unique_emails);
      }
    } catch (err) {
      toast.error("Failed to fetch email logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueEmails = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-logs/unique-emails`);
      setUniqueEmails(res.data.emails || []);
    } catch (err) {
      console.error("Failed to fetch unique emails");
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchUniqueEmails();
  }, [page, statusFilter, toEmailFilter, categoryFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const toggleSelect = (logId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(logId)) {
      newSelected.delete(logId);
    } else {
      newSelected.add(logId);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === logs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(logs.map(l => l.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API}/admin/email-logs/delete`, {
        log_ids: Array.from(selectedIds)
      });
      toast.success(`Deleted ${selectedIds.size} email log(s)`);
      setSelectedIds(new Set());
      setDeleteDialogOpen(false);
      fetchLogs();
    } catch (err) {
      toast.error("Failed to delete email logs");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/admin/email-logs/delete-all`);
      toast.success(res.data.message || "All email logs deleted");
      setSelectedIds(new Set());
      setDeleteAllDialogOpen(false);
      fetchLogs();
      fetchUniqueEmails();
    } catch (err) {
      toast.error("Failed to delete email logs");
    } finally {
      setDeleting(false);
    }
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const extractTextFromHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <div className="space-y-6" data-testid="email-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Logs</h1>
          <p className="text-slate-500 mt-1">View all emails sent by the system</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {total} total emails
          </Badge>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => setDeleteAllDialogOpen(true)}
            disabled={total === 0}
            data-testid="delete-all-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete All
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by email or subject..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="email-search-input"
                  />
                </div>
                <Button type="submit" variant="secondary" data-testid="search-btn">Search</Button>
              </form>
            </div>
            
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
                <SelectTrigger className="w-40" data-testid="category-filter">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="logs">Logs</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="CRM">CRM</SelectItem>
                  <SelectItem value="others">Others</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={toEmailFilter} onValueChange={(v) => { setToEmailFilter(v); setPage(1); }}>
                <SelectTrigger className="w-64" data-testid="to-email-filter">
                  <SelectValue placeholder="Filter by recipient" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Recipients</SelectItem>
                  {uniqueEmails.map(email => (
                    <SelectItem key={email} value={email}>{email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>

              {selectedIds.size > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected ({selectedIds.size})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No email logs found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 pr-2 w-10">
                        <Checkbox
                          checked={selectedIds.size === logs.length && logs.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="pb-3 font-medium text-slate-600 text-sm">To</th>
                      <th className="pb-3 font-medium text-slate-600 text-sm">Category</th>
                      <th className="pb-3 font-medium text-slate-600 text-sm">Subject</th>
                      <th className="pb-3 font-medium text-slate-600 text-sm">Date/Time</th>
                      <th className="pb-3 font-medium text-slate-600 text-sm">Status</th>
                      <th className="pb-3 font-medium text-slate-600 text-sm w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`email-row-${log.id}`}>
                        <td className="py-3 pr-2">
                          <Checkbox
                            checked={selectedIds.has(log.id)}
                            onCheckedChange={() => toggleSelect(log.id)}
                            data-testid={`select-log-${log.id}`}
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-sm font-mono">{log.to_email}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <CategoryBadge category={log.category} />
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-sm font-medium">{truncateText(log.subject, 50)}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-sm text-slate-600">
                            {format(parseISO(log.created_at), "MMM d, yyyy h:mm a")}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <StatusBadge status={log.status} />
                        </td>
                        <td className="py-3">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openDetail(log)}
                            data-testid={`view-email-${log.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <EmailDetailDialog 
        log={selectedLog} 
        open={detailOpen} 
        onClose={() => setDetailOpen(false)} 
      />
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteSelected}
        count={selectedIds.size}
        isAll={false}
      />
      
      <DeleteConfirmDialog
        open={deleteAllDialogOpen}
        onClose={() => setDeleteAllDialogOpen(false)}
        onConfirm={handleDeleteAll}
        count={total}
        isAll={true}
      />
    </div>
  );
}
